/**
 * FeedAttachments — renderização de anexos de um post do feed (E17.1.3).
 *
 * Três tipos (``kind`` derivado do MIME no backend):
 * - ``image`` → grade clicável; clique abre um lightbox (Dialog) com prev/next.
 * - ``video`` → ``VideoAttachment`` (PR 1) na variante ``cartao``: miniatura +
 *   botão de play; o ``<video>`` só é montado no DOM depois do clique — nada
 *   baixa ao abrir a página (ver o comentário do próprio componente).
 * - ``file``  → link de download (ícone + nome + tamanho humanizado).
 *
 * As URLs são signed (transitórias) — vêm prontas no ``attachment.url``.
 */

import { useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { DriveAttachmentChip } from "@/components/documents/DriveAttachmentChip";
import { VideoAttachment } from "@/components/common/VideoAttachment";
import { FeedAttachment } from "@/types/feed";

const humanSize = (bytes: number): string => {
  if (!bytes || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export interface FeedAttachmentsProps {
  attachments: FeedAttachment[];
}

export function FeedAttachments({ attachments }: FeedAttachmentsProps) {
  // Anexos do Drive (E14.2.5): referência viva (document_id, sem url/storage_key)
  // — renderizados pelo chip (abrir/baixar/editar via endpoints de documento).
  const driveDocs = attachments.filter((a) => a.document_id);
  const images = attachments.filter((a) => a.kind === "image" && a.url);
  const videos = attachments.filter((a) => a.kind === "video" && a.url);
  // T10 (ruling da revisão final, 19/09/2026): "arquivo" é o que SOBRA depois
  // de Drive/imagem-com-url/vídeo-com-url — não mais "kind === file OU sem
  // url". A regra antiga deixava sumir (sem cair em NENHUM grupo) um anexo
  // sem `kind` — `kind` é obrigatório no TYPE, mas nada garante isso em
  // runtime (JSON do backend) — que tivesse `url`: não batia "image"/"video"
  // (kind não confere) nem a condição antiga de `files` (kind não era
  // literalmente "file" E `url` existia, então `!a.url` também era falso).
  // Com o "resto", ele cai aqui — como link de arquivo, nunca invisível.
  // Uploads sem url (signed-url indisponível) continuam caindo aqui também
  // — melhor mostrar o nome do que sumir silenciosamente. Anexos do Drive já
  // foram tratados acima.
  const files = attachments.filter(
    (a) => !a.document_id && !(a.kind === "image" && a.url) && !(a.kind === "video" && a.url)
  );

  const [lightbox, setLightbox] = useState<number | null>(null);

  const openImage = (storageKey: string) => {
    const idx = images.findIndex((a) => a.storage_key === storageKey);
    if (idx >= 0) setLightbox(idx);
  };

  const step = (delta: number) => {
    setLightbox((prev) => {
      if (prev == null || images.length === 0) return prev;
      return (prev + delta + images.length) % images.length;
    });
  };

  if (attachments.length === 0) return null;

  const current = lightbox != null ? images[lightbox] : null;

  return (
    <div className="space-y-2" data-testid="feed-attachments">
      {driveDocs.map((a) => (
        <DriveAttachmentChip
          key={a.document_id as string}
          documentId={a.document_id as string}
          name={a.filename}
          sizeBytes={a.size_bytes}
          fileExtension={a.filename.includes(".") ? a.filename.split(".").pop() : undefined}
        />
      ))}

      {/* A imagem aparece INTEIRA, sem recorte (pedido-0800b59f, 20/10/2026): até
          ali era uma faixa de 160 px com `object-cover` e a arte das Publicações
          só se via clicando. O teto de 80vh faz a arte quadrada caber na tela sem
          rolar; o `w-fit` faz a moldura abraçar a imagem (sem faixa cinza dos
          lados) e o `items-start` impede a grade de esticar a moldura da imagem
          mais baixa até a altura da vizinha. */}
      {images.length > 0 && (
        <div
          className={cn(
            "grid items-start gap-1.5",
            images.length === 1 ? "grid-cols-1" : "grid-cols-2"
          )}
          data-testid="feed-attachment-images"
        >
          {images.map((a) => (
            <button
              key={a.storage_key}
              type="button"
              onClick={() => openImage(a.storage_key)}
              className="mx-auto w-fit max-w-full overflow-hidden rounded-md border bg-muted/30"
              data-testid="feed-attachment-image"
            >
              <img
                src={a.url ?? ""}
                alt={a.alt || a.filename}
                loading="lazy"
                className="block h-auto max-h-[80vh] w-auto max-w-full"
              />
            </button>
          ))}
        </div>
      )}

      {videos.map((a) => (
        <div key={a.storage_key} data-testid="feed-attachment-video">
          <VideoAttachment
            src={a.url ?? ""}
            posterSrc={a.poster_url ?? undefined}
            filename={a.filename}
            durationSec={a.duration_sec}
            largura={a.width}
            altura={a.height}
            variante="cartao"
          />
        </div>
      ))}

      {files.map((a) => {
        const row = (
          <>
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{a.filename}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {humanSize(a.size_bytes)}
            </span>
            {a.url && <Download className="h-4 w-4 shrink-0 text-muted-foreground" />}
          </>
        );
        const className = "flex items-center gap-2 rounded-md border px-3 py-2 text-sm";
        // Sem url (signed-url indisponível) → não-navegável (evita ir pra "#").
        return a.url ? (
          <a
            key={a.storage_key}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            download={a.filename}
            className={`${className} hover:bg-accent`}
            data-testid="feed-attachment-file"
          >
            {row}
          </a>
        ) : (
          <div
            key={a.storage_key}
            className={`${className} text-muted-foreground`}
            data-testid="feed-attachment-file"
          >
            {row}
          </div>
        );
      })}

      <Dialog open={lightbox != null} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-3xl" data-testid="feed-lightbox">
          <DialogTitle className="sr-only">{current?.filename ?? "Imagem"}</DialogTitle>
          <DialogDescription className="sr-only">
            Visualização ampliada do anexo do post.
          </DialogDescription>
          {current && (
            <div className="flex flex-col items-center gap-2">
              <img
                src={current.url ?? ""}
                alt={current.alt || current.filename}
                className="max-h-[70vh] w-auto rounded"
              />
              {images.length > 1 && (
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label="Imagem anterior"
                    data-testid="feed-lightbox-prev"
                    className="rounded-full p-1 hover:bg-accent"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {(lightbox ?? 0) + 1} / {images.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label="Próxima imagem"
                    data-testid="feed-lightbox-next"
                    className="rounded-full p-1 hover:bg-accent"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FeedAttachments;
